import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import React from "react";

const featureInfo1: FeatureInfo = {
    mainTitle: "Real-time customer updates via WhatsApp",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/shooper-show.png",
    featureInfoList: [
        "Send instant WhatsApp alerts for dispatch, transit, delays, and delivery confirmations",
        "Reduce WISMO/WISMR (“Where is my order/return?”) inquiries and support workload",
        "Customize message templates for different delivery scenarios",
        "Boost customer satisfaction and trust with proactive communication",
        "Ensure 24/7 availability with chatbots that handle order status inquiries even after business hours",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Unified team inbox for driver & dispatch coordination",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/team-inbox.png",
    featureInfoList: [
        "Assign chats to support agents or dispatch teams automatically",
        "Enable seamless communication between fleet managers, drivers, and operations",
        "Tag and filter chats by delivery status, location, or issue type",
        "Keep your entire transportation team aligned with internal notes and collaboration.",
        "Ensure no messages go unanswered with always-on chatbots to support your dispatch team at any hour",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Automated workflows to save time & cut manual tasks",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/workflow.png",
    featureInfoList: [
        "Deploy WhatsApp chatbots to answer FAQs about shipping, delivery slots, or pickup timings",
        "Automate order confirmations, returns, and rescheduling flows",
        "Set up rule-based routing to direct chats to the right department instantly",
        "Reduce first-response time and ticket backlog during peak periods",
        "Provide automated assistance at any time, even on weekends and holidays, for customers tracking orders or rescheduling pickups",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Broadcast tools for alerts and announcements",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/shooper-market.png",
    featureInfoList: [
        "Use WhatsApp broadcasts to send traffic alerts, route changes, or safety instructions",
        "Share delivery notifications with customers in a few clicks",
        "Segment contacts by region, delivery type, or service level",
        "Ensure time-sensitive info reaches the right people, fast",
        "Combine broadcasts with always-available bots to ensure updates are acknowledged and followed up without delay",
    ],
};

const featureInfo5: FeatureInfo = {
    mainTitle: "Seamless integration with CRMs, TMS, & order systems",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/seamless-integration.png",
    featureInfoList: [
        "Integrate with CRMs like Zoho, HubSpot, or custom ERPs effortlessly",
        "Sync delivery status, contact info, and support history in real-time",
        "Trigger WhatsApp messages based on transportation updates or API events",
        "Maintain complete visibility across the customer journey and delivery lifecycle.",
        "Enable around-the-clock system-driven communication through automated WhatsApp alerts",
    ],
};

const herosectionData: HerosectionProps = {
    chipTitle: "LOGISTICS & SHIPPING",
    title: "Streamline operations with Gallabox’s WhatsApp business solution for logistics & shipping",
    description:
        "Deliver faster, communicate better, and manage transportation at scale with an all-in-one WhatsApp Business solution tailored for logistics and shipping companies.",
    primaryButtonText: "Start free trial",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/hero-image.png",
};

const sampleProductCardData: Array<Product> = [
    {
        title: "Reduce missed deliveries with faster communication",
        description: "Minimize delivery failures by keeping drivers and customers in sync through instant WhatsApp updates.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/click-whatsapp-ads.png",
    },
    {
        title: "Increase operational efficiency through automation",
        description: "Streamline workflows, eliminate manual follow-ups, and handle more deliveries without growing your team.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/broadcast.png",
    },
    {
        title: "Drive customer loyalty with proactive WhatsApp updates",
        description: "Build trust and improve post-purchase experiences by keeping customers informed at every stage of the journey.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/drip-marketing.png",
    },
    {
        title: "Provide 24/7 support with smart chatbots",
        description: "From routing chats to answering FAQs, let Gallabox handle repetitive queries day or night, so your team can focus on critical ops.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/drip-marketing.png",
    },
];

const Logistic: React.FC = () => {
    return (
        <NewLayout
            title="Enhance WhatsApp Business Solution for Logistics & Shipping"
            description="Streamline your transportation management with a WhatsApp business software for logistics. Enhance team communication and automate updates."
            canonical=" https://gallabox.com/industry/logistics-transportation/whatsapp"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/industry-logos/education/industrie-education-og.png" }}
            schema={{
                heroOrOgImageAlt: "education-industry-image",
                heroOrOgImageDescription:
                    "From the first student query to filling in the admissions form to making the course payment, do it all using Gallabox's WhatsApp automation features.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/industries/logistics/logistics-og.png",
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">Trusted by 10,000+ businesses</h3>
                <ClientLogos />
            </section>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} />
            <FeatureInfoCard {...featureInfo5} />
            <ProductSection
                title="Improve response times. Increase deliveries. Delight customers."
                description="Improve response times. Increase deliveries. Delight customers."
                productCardData={sampleProductCardData}
            />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Ready to Scale Your Logistics with WhatsApp?" />
        </NewLayout>
    );
};

export default Logistic;
